import { useContext, useEffect } from 'react';
import {
  ApiFilterOperation,
  HookedSource,
  UserContext,
  useScrollableSource,
} from '@eas/common-web';
import { TechnicalObject } from '@models';
import { EvidenceApiUrl, Permission } from '@enums';

export const DEFECTS_SOURCE = 'DEFECTS_SOURCE';
export const ACTIVITIES_SOURCE = 'ACTIVITIES_SOURCE';
export const OPERATIONS_SOURCE = 'OPERATIONS_SOURCE';
export const MISMATCH_SOURCE = 'MISMATCH_SOURCE';

function useMismatchSource(): HookedSource<TechnicalObject> {
  const source = useScrollableSource();
  const { hasPermission } = useContext(UserContext);

  return {
    [MISMATCH_SOURCE]: {
      source,
      shouldLoad: () => hasPermission(Permission.MISMATCH_LIST),
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.MISMATCH}/list`);
        source.setParams({
          size: 15,
          filters: [
            {
              field: 'technicalObject.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
          ],
        });
      },
    },
  };
}

function useDefectSource(workOrderId?: string): HookedSource<TechnicalObject> {
  const source = useScrollableSource();
  const { hasPermission } = useContext(UserContext);

  return {
    [DEFECTS_SOURCE]: {
      source,
      shouldLoad: () => hasPermission(Permission.DEFECT_LIST),
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.DEFECTS}/list`);
        source.setParams({
          size: 15,
          filters: [
            {
              field: 'technicalObject.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
            ...(workOrderId
              ? [
                  {
                    field: 'workOrder.id',
                    operation: ApiFilterOperation.EQ,
                    value: workOrderId,
                  },
                ]
              : []),
          ],
        });
      },
    },
  };
}

function useActivitiesSource(
  workOrderId?: string
): HookedSource<TechnicalObject> {
  const source = useScrollableSource();
  const { hasPermission } = useContext(UserContext);

  return {
    [ACTIVITIES_SOURCE]: {
      source,
      shouldLoad: () => hasPermission(Permission.ACTIVITY_LIST),
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ACTIVITIES}/list`);
        source.setParams({
          size: 15,
          filters: [
            {
              field: 'technicalObject.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
            ...(workOrderId
              ? [
                  {
                    field: 'workOrder.id',
                    operation: ApiFilterOperation.EQ,
                    value: workOrderId,
                  },
                ]
              : []),
          ],
        });
      },
    },
  };
}

function useOperationsSource(
  workOrderId?: string
): HookedSource<TechnicalObject> {
  const source = useScrollableSource();
  const { hasPermission } = useContext(UserContext);

  return {
    [OPERATIONS_SOURCE]: {
      source,
      shouldLoad: () => hasPermission(Permission.OPERATION_LIST),
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.OPERATIONS}/list`);
        source.setParams({
          size: 15,
          filters: [
            {
              field: 'technicalObject.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
            ...(workOrderId
              ? [
                  {
                    field: 'workOrder.id',
                    operation: ApiFilterOperation.EQ,
                    value: workOrderId,
                  },
                ]
              : []),
          ],
        });
      },
    },
  };
}

export function useTechnicalObjectHookedSources(
  workOrderId?: string,
  workOrderZ105?: boolean
): HookedSource<TechnicalObject> {
  const defects = useDefectSource(workOrderZ105 ? workOrderId : undefined);
  const activities = useActivitiesSource(workOrderId);
  const operations = useOperationsSource(workOrderId);
  const mismatches = useMismatchSource();

  return { ...defects, ...activities, ...operations, ...mismatches };
}
/**
 *
 * @param id Technical object id
 * @param workOrderId work order id (internal, not workOrderId!!)
 * @param workOrderZ105 is workorder Z105
 */
export function useManualLoadTechnicalObjectHookedSources(
  id: string,
  workOrderId?: string,
  workOrderZ105?: boolean
) {
  const hookedSources = useTechnicalObjectHookedSources(
    workOrderId,
    workOrderZ105
  );
  useEffect(() => {
    Object.keys(hookedSources).forEach((key) => {
      hookedSources[key]?.modifyBeforeLoad?.({ id, workOrderId } as any);
      hookedSources[key]?.source?.reset();
      hookedSources[key]?.source?.loadMore();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return hookedSources;
}
